import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  CreditCard,
  IndianRupee,
  PackageCheck,
  RotateCcw,
  Ruler,
  ShieldCheck,
  Shirt,
  Star,
  Truck,
} from "lucide-react";
import { useCart } from "../../context/CartContext";
import { formatINR, getProductCategories } from "../../lib/utils-cynos";
import CountdownTimer from "../../components/CountdownTimer";
import Loader from "../../components/common/Loader";
import PriceDisplay from "../../components/common/PriceDisplay";
import WishlistButton from "../../components/common/WishlistButton";
import ProductGallery from "../../components/product/ProductGallery";
import SizeSelector from "../../components/product/SizeSelector";
import ColorSelector from "../../components/product/ColorSelector";
import QuantitySelector from "../../components/product/QuantitySelector";
import RelatedProducts from "../../components/product/RelatedProducts";
import ProductShareButton from "../../components/product/ProductShareButton";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../../components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { getProductBySlug, listRelatedProducts } from "../../services/productService";
import { estimateDeliveryByPincode } from "../../services/shippingService";
import { FAQS, REVIEWS, colorImageKey } from "./productDetailData";
import { setPageMeta } from "../../utils/seo";
import { getProductFitLabel } from "../../utils/productFit";

const BASE_PRODUCT_HIGHLIGHTS = [
  "Premium Cotton Fabric",
  "Long-lasting Print",
  "Unisex Style",
  "Comfortable Daily Wear",
];

const SIZE_ROWS = [
  ["S", "38", "27"],
  ["M", "40", "28"],
  ["L", "42", "29"],
  ["XL", "44", "30"],
  ["XXL", "46", "31"],
];

const RECENTLY_VIEWED_KEY = "cynos_recently_viewed_v1";

const productSnapshot = (product) => ({
  id: product.id,
  slug: product.slug,
  seo_slug: product.seo_slug,
  name: product.name,
  price: product.price,
  original_price: product.original_price,
  image: product.image,
  images: product.images || [],
  category: product.category,
  categories: product.categories || [],
  badges: product.badges || [],
  sizes: product.sizes || [],
  colors: product.colors || [],
  active_status: product.active_status,
  stock_count: product.stock_count,
  color_images: product.color_images || {},
  variant_skus: product.variant_skus || {},
  qikink_sku: product.qikink_sku || "",
});

const readRecentlyViewed = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || "[]");
  } catch {
    return [];
  }
};

const ProductDetailsPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [selectionError, setSelectionError] = useState("");
  const { addItem } = useCart();

  useEffect(() => {
    setProduct(null);
    setActiveImg(0);
    setSize("");
    setColor("");
    setQty(1);
    setSelectionError("");
    getProductBySlug(slug).then((data) => {
      setProduct(data);
      setSize(data.sizes?.[1] || data.sizes?.[0] || "M");
      setColor(data.colors?.[0] || "");
      listRelatedProducts(data).then(setRelated).catch(() => setRelated([]));
      const previous = readRecentlyViewed().filter((item) => item.id !== data.id && item.slug !== data.slug);
      setRecentlyViewed(previous.slice(0, 4));
      try {
        localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify([productSnapshot(data), ...previous].slice(0, 8)));
      } catch {}
    });
  }, [slug]);

  useEffect(() => {
    setActiveImg(0);
  }, [color]);

  useEffect(() => {
    if (!product) return;
    const canonicalSlug = product.seo_slug || product.slug;
    const canonical = `${window.location.origin}/product/${canonicalSlug}`;
    const image = product.images?.[0] || product.image || "";
    const isInactive = Number(product.active_status) === 0;
    const isComingSoonProduct = Number(product.active_status) === 2;
    const sku = product.store_sku || product.qikink_sku || product.product_sku || product.sku || product.id;
    const availability = isComingSoonProduct
      ? "https://schema.org/PreOrder"
      : product.stock_count > 0
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock";
    const productSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      image: product.images || [],
      description: product.meta_description || product.description,
      sku,
      brand: { "@type": "Brand", name: "Cynos" },
      offers: {
        "@type": "Offer",
        url: canonical,
        priceCurrency: "INR",
        price: product.price,
        availability,
        itemCondition: "https://schema.org/NewCondition",
      },
    };
    if (product.rating && product.review_count) {
      productSchema.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: product.rating,
        reviewCount: product.review_count,
      };
    }
    setPageMeta({
      title: product.meta_title || `${product.name} | Cynos`,
      description: product.meta_description || product.description,
      keywords: product.meta_keywords,
      canonical,
      robots: isInactive ? "noindex,nofollow" : "index,follow",
      og: {
        type: "product",
        title: product.meta_title || product.name,
        description: product.meta_description || product.description,
        image,
        url: canonical,
        site_name: "Cynos",
      },
      twitter: {
        card: "summary_large_image",
        title: product.meta_title || product.name,
        description: product.meta_description || product.description,
        image,
      },
      jsonLd: productSchema,
      jsonLdId: "product-json-ld",
      removeJsonLdIds: ["page-json-ld"],
    });
    return () => {
      document.getElementById("product-json-ld")?.remove();
    };
  }, [product]);

  if (!product) return <div className="pt-32 px-6"><Loader label="Loading drop..." /></div>;

  const availableColors = product.colors || [];
  const selectedColorImageKey = colorImageKey(product.color_images, color);
  const galleryImages = selectedColorImageKey ? product.color_images[selectedColorImageKey] : product.images;
  const isInactive = Number(product.active_status) === 0;
  const isComingSoon = Number(product.active_status) === 2;
  const isUnavailable = isInactive || isComingSoon;

  const canAddSelection = () => {
    if (isInactive) {
      setSelectionError("This product is currently unavailable.");
      return false;
    }
    if (isComingSoon) {
      setSelectionError("This drop is coming soon. Add to cart is disabled for now.");
      return false;
    }
    if (!size) {
      setSelectionError("Please select a size.");
      return false;
    }
    if (availableColors.length > 0 && !color) {
      setSelectionError("Please select a color.");
      return false;
    }
    setSelectionError("");
    return true;
  };

  const handleAddToCart = () => {
    if (!canAddSelection()) return;
    addItem(product, size, qty, color);
  };

  const handleBuyNow = () => {
    if (!canAddSelection()) return;
    addItem(product, size, qty, color);
    setTimeout(() => { window.location.href = "/checkout"; }, 100);
  };

  const canWishlistSelection = () => {
    if (!size) {
      setSelectionError("Please select a size before saving.");
      return false;
    }
    if (availableColors.length > 0 && !color) {
      setSelectionError("Please select a color before saving.");
      return false;
    }
    setSelectionError("");
    return true;
  };

  return (
    <main className="pb-36 pt-16 md:pb-24 md:pt-20" data-testid="product-detail-page">
      <div className="grid grid-cols-1 gap-0 px-0 md:grid-cols-[minmax(0,1.02fr)_minmax(380px,0.98fr)] md:gap-8 md:px-8 lg:px-12">
        <ProductGallery images={galleryImages || []} activeIndex={activeImg} onActiveIndexChange={setActiveImg} alt={product.name} />

        <div className="mt-6 px-4 md:mt-0 md:px-0">
          <div className="flex flex-wrap gap-2">
            {getProductCategories(product).map((category) => (
              <span key={category} className="inline-flex rounded-[8px] border border-cynos-red/35 bg-cynos-red/10 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-cynos-red md:text-xs">
                {category}
              </span>
            ))}
          </div>
          <h1 className="mt-3 max-w-2xl break-words font-anton text-[2rem] uppercase leading-[0.95] md:text-[2.65rem] lg:text-[3rem]">
            {product.name}
          </h1>

          <div className="mt-4 flex items-center gap-2">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => <Star key={i} size={14} fill={i < Math.round(product.rating) ? "#FF3333" : "transparent"} stroke="#FF3333" />)}
            </div>
            <span className="font-mono text-xs text-zinc-400">{product.rating} ({product.review_count} reviews)</span>
          </div>

          <div className="mt-5">
            <PriceDisplay price={product.price} originalPrice={product.original_price} size="lg" />
          </div>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">Inclusive of all taxes</p>

          <div className="mt-4"><CountdownTimer /></div>

          {isComingSoon && (
            <div className="mt-4 border border-cynos-red/50 bg-cynos-red/15 text-red-100 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em]" data-testid="coming-soon-product">
              Coming Soon - add to cart is disabled until this drop goes live.
            </div>
          )}

          {isInactive && (
            <div className="mt-4 border border-white/15 bg-white/5 text-zinc-300 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em]" data-testid="inactive-product">
              Currently unavailable.
            </div>
          )}

          {!isUnavailable && product.stock_count <= 10 && (
            <div className="mt-4 border border-cynos-red/40 bg-cynos-red/10 text-cynos-red px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em]" data-testid="stock-urgency">
              Only {product.stock_count} left in stock - restocking unlikely
            </div>
          )}

          <div className="mt-7">
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-xs uppercase tracking-[0.2em]">Select Size</span>
              <SizeGuideDialog />
            </div>
            <div data-testid="size-selector">
              <SizeSelector
                sizes={product.sizes || []}
                value={size}
                onChange={(next) => { setSize(next); setSelectionError(""); }}
                allLabel=""
                testPrefix="size-opt"
              />
            </div>
          </div>

          {availableColors.length > 0 && (
            <div className="mt-6">
              <span className="font-mono text-xs uppercase tracking-[0.2em]">Select Color</span>
              <div className="mt-3" data-testid="color-selector">
                <ColorSelector
                  colors={availableColors}
                  value={color}
                  onChange={(next) => { setColor(next); setSelectionError(""); }}
                  testPrefix="color-opt"
                />
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center gap-4">
            <span className="font-mono text-xs uppercase tracking-[0.2em]">Qty</span>
            <QuantitySelector value={qty} onChange={setQty} />
          </div>

          {selectionError && (
            <div className="mt-5 border border-cynos-red/40 bg-cynos-red/10 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-red-200" data-testid="selection-error">
              {selectionError}
            </div>
          )}

          <div className="mt-7 grid grid-cols-[58px_minmax(0,1fr)_minmax(0,1fr)_58px] gap-2 rounded-[8px] border border-white/10 bg-white/[0.02] p-2 md:grid-cols-[64px_minmax(0,1fr)_minmax(0,1fr)_170px] md:gap-3 md:p-3" data-testid="product-action-row">
            <WishlistButton
              product={product}
              color={color}
              size={size}
              beforeToggle={canWishlistSelection}
              variant="red"
              className="h-full min-h-[54px] rounded-[8px] md:min-h-[58px]"
            />
            <button onClick={handleAddToCart} disabled={isUnavailable} className="min-h-[54px] whitespace-nowrap rounded-[8px] border border-white px-2 py-3 font-mono text-[10px] uppercase tracking-[0.14em] text-white transition-colors hover:bg-white hover:text-black disabled:cursor-not-allowed disabled:border-white/10 disabled:text-zinc-500 disabled:hover:bg-transparent md:min-h-[58px] md:text-xs md:tracking-[0.18em]" data-testid="add-to-cart-btn">
              {isComingSoon ? "Coming Soon" : isInactive ? "Unavailable" : "Add To Cart"}
            </button>
            <button onClick={handleBuyNow} disabled={isUnavailable} className="min-h-[54px] whitespace-nowrap rounded-[8px] bg-cynos-red px-2 py-3 font-mono text-[10px] uppercase tracking-[0.14em] text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:bg-zinc-800 disabled:text-zinc-500 md:min-h-[58px] md:text-xs md:tracking-[0.18em]" data-testid="buy-now-btn">
              {isComingSoon ? "Coming Soon" : isInactive ? "Unavailable" : "Buy Now"}
            </button>
            <ProductShareButton
              product={product}
              compact
              className="h-full min-h-[54px] rounded-[8px] px-2 md:min-h-[58px] md:px-4"
              labelClassName="hidden md:inline"
            />
          </div>

          <DeliveryEstimate />

          <section className="mt-7 rounded-[8px] border border-white/10 bg-white/[0.02] p-5" data-testid="product-description">
            <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white">Description</h2>
            <p className="mt-3 leading-7 text-zinc-300">{product.description}</p>
          </section>

          <ProductHighlights product={product} />

          <div className="mt-7 grid grid-cols-2 gap-3 border-t border-white/10 pt-5">
            <TrustItem icon={CreditCard} label="Secure Payment" />
            <TrustItem icon={RotateCcw} label="Easy Returns" />
            <TrustItem icon={IndianRupee} label="COD Available" />
            <TrustItem icon={ShieldCheck} label="Quality Checked" />
          </div>

          <ProductFaq />
        </div>
      </div>

      <Reviews />
      <RelatedProducts products={related} title="Related Drops" testId="related-products" />
      <RelatedProducts products={recentlyViewed} title="Recently Viewed" testId="recently-viewed-products" />

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-white/10 bg-black/95 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur md:hidden" data-testid="sticky-mobile-bar">
        <div className="mb-2 flex items-center justify-between gap-3">
          <div>
            <div className="font-anton text-xl leading-none">{formatINR(product.price)}</div>
            {product.original_price > product.price && (
              <div className="font-mono text-[10px] text-zinc-500 line-through">{formatINR(product.original_price)}</div>
            )}
          </div>
          <div className="text-right font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
            Size {size || "M"}{color ? ` / ${color}` : ""}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={handleAddToCart} disabled={isUnavailable} className="min-h-[50px] rounded-[8px] border border-white/30 px-3 font-mono text-[11px] uppercase tracking-[0.16em] text-white disabled:border-white/10 disabled:text-zinc-500" data-testid="sticky-add-to-cart-btn">
            {isComingSoon ? "Soon" : isInactive ? "Unavailable" : "Add to Cart"}
          </button>
          <button onClick={handleBuyNow} disabled={isUnavailable} className="min-h-[50px] rounded-[8px] bg-cynos-red px-3 font-mono text-[11px] uppercase tracking-[0.16em] text-white disabled:bg-zinc-800 disabled:text-zinc-500" data-testid="sticky-buy-now-btn">
            {isComingSoon ? "Coming Soon" : isInactive ? "Unavailable" : "Buy Now"}
          </button>
        </div>
      </div>
    </main>
  );
};

const productHighlightsFor = (product) => [
  getProductFitLabel(product),
  ...BASE_PRODUCT_HIGHLIGHTS,
];

const ProductHighlights = ({ product }) => (
  <section className="mt-8 rounded-[8px] border border-white/10 bg-cynos-card p-5" data-testid="product-highlights">
    <div className="mb-4 flex items-center gap-2">
      <Shirt size={18} className="text-cynos-red" />
      <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white">Product Highlights</h2>
    </div>
    <div className="grid gap-3 sm:grid-cols-2">
      {productHighlightsFor(product).map((item) => (
        <div key={item} className="flex items-center gap-2 text-sm text-zinc-300">
          <CheckCircle2 size={16} className="shrink-0 text-cynos-red" />
          <span>{item}</span>
        </div>
      ))}
    </div>
  </section>
);

const DeliveryEstimate = () => {
  const [pincode, setPincode] = useState("");
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState("Enter a 6-digit Indian pincode for delivery estimate.");
  const isValid = /^[1-8]\d{5}$/.test(pincode);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setResult(null);
    if (!isValid) {
      setStatus("error");
      setMessage("Enter a valid 6-digit Indian pincode.");
      return;
    }

    setStatus("loading");
    setMessage("Checking delivery availability...");
    try {
      const data = await estimateDeliveryByPincode(pincode);
      setResult(data);
      if (data.available) {
        setStatus("success");
        setMessage(data.message || `Delivery available to ${pincode}.`);
      } else {
        setStatus("error");
        setMessage(data.message || `Delivery is not available to ${pincode}.`);
      }
    } catch (err) {
      setStatus("error");
      setMessage(err?.response?.data?.detail || err?.message || "Could not check delivery right now. Please try again.");
    }
  };

  const estimateText = result?.available && result.estimated_min_days && result.estimated_max_days
    ? `Estimated delivery: ${result.estimated_min_days}-${result.estimated_max_days} business days`
    : "";
  const deliveryMeta = result?.available
    ? [
        [result.city, result.state].filter(Boolean).join(", "),
        result.cod_available ? "COD available" : "Prepaid only",
      ].filter(Boolean).join(" - ")
    : "";

  return (
    <section className={`mt-8 rounded-[8px] border p-5 transition-colors ${
      status === "success"
        ? "border-emerald-400/30 bg-emerald-400/5"
        : status === "error"
          ? "border-cynos-red/35 bg-cynos-red/5"
          : "border-white/10 bg-cynos-card"
    }`} data-testid="delivery-estimate">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] border border-cynos-red/35 bg-cynos-red/10 text-cynos-red">
          <Truck size={18} />
        </div>
        <div>
          <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white">Delivery Estimate</h2>
          <p className="mt-1 text-xs leading-5 text-zinc-500">Check serviceability, delivery timeline, and COD availability.</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <input
          value={pincode}
          onChange={(event) => {
            setPincode(event.target.value.replace(/\D/g, "").slice(0, 6));
            setStatus("idle");
            setResult(null);
            setMessage("Enter a 6-digit Indian pincode for delivery estimate.");
          }}
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="Enter pincode"
          className="min-h-[50px] min-w-0 rounded-[8px] border border-white/15 bg-black px-4 font-mono text-xs uppercase tracking-[0.16em] outline-none placeholder:text-zinc-600 focus:border-white"
          data-testid="pincode-input"
        />
        <button
          type="submit"
          disabled={status === "loading"}
          className="min-h-[50px] rounded-[8px] bg-white px-5 font-mono text-[10px] uppercase tracking-[0.18em] text-black transition hover:bg-zinc-200 disabled:cursor-wait disabled:opacity-60"
          data-testid="pincode-check-btn"
        >
          {status === "loading" ? "Checking" : "Check"}
        </button>
      </form>

      <div className={`mt-4 rounded-[8px] border px-4 py-3 ${
        status === "success"
          ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-200"
          : status === "error"
            ? "border-cynos-red/25 bg-cynos-red/10 text-red-200"
            : "border-white/10 bg-black/30 text-zinc-400"
      }`} data-testid="delivery-result">
        <div className="flex items-start gap-2">
          {status === "success" ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : status === "error" ? <AlertCircle size={16} className="mt-0.5 shrink-0" /> : <PackageCheck size={16} className="mt-0.5 shrink-0" />}
          <div className="text-sm leading-6">
            <p>{message}</p>
            {estimateText && <p className="font-medium">{estimateText}</p>}
            {deliveryMeta && (
              <p className="text-xs uppercase tracking-[0.16em] opacity-80">
                {deliveryMeta}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const SizeGuideDialog = () => (
  <Dialog>
    <DialogTrigger asChild>
      <button className="font-mono text-xs uppercase tracking-[0.2em] underline underline-offset-4 inline-flex items-center gap-1 hover:text-cynos-red" data-testid="size-guide-trigger">
        <Ruler size={12} /> Size Guide
      </button>
    </DialogTrigger>
    <DialogContent className="max-w-lg rounded-[8px] border-white/10 bg-cynos-card">
      <DialogHeader>
        <DialogTitle className="font-anton text-3xl uppercase">Size Guide</DialogTitle>
      </DialogHeader>
      <div className="text-sm">
        <table className="w-full text-left border-collapse">
          <thead className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">
            <tr><th className="py-2 border-b border-white/10">Size</th><th className="border-b border-white/10">Chest (in)</th><th className="border-b border-white/10">Length (in)</th></tr>
          </thead>
          <tbody>
            {SIZE_ROWS.map((row) => (
              <tr key={row[0]} className="border-b border-white/10"><td className="py-2 font-bold">{row[0]}</td><td>{row[1]}</td><td>{row[2]}</td></tr>
            ))}
          </tbody>
        </table>
        <p className="text-zinc-400 mt-4 text-xs">Measurements taken from a flat-lay. Allow +/-0.5 inch tolerance.</p>
      </div>
    </DialogContent>
  </Dialog>
);

const ProductFaq = () => (
  <div className="mt-10">
    <h3 className="mb-4 font-anton text-3xl uppercase">FAQ</h3>
    <Accordion type="single" collapsible className="border-t border-white/10">
      {FAQS.map((faq, index) => (
        <AccordionItem key={faq.q} value={`f${index}`} className="border-b border-white/10">
          <AccordionTrigger className="text-left text-base font-bold uppercase hover:no-underline" data-testid={`faq-${index}`}>{faq.q}</AccordionTrigger>
          <AccordionContent className="text-zinc-400 text-sm">{faq.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </div>
);

const Reviews = () => (
  <section className="mt-14 px-4 md:mt-16 md:px-8 lg:px-12" data-testid="product-reviews">
    <h2 className="mb-5 font-anton text-[clamp(2rem,8vw,3rem)] uppercase leading-none md:text-5xl">Customer Photos & Reviews</h2>
    <div className="grid gap-4 md:grid-cols-3">
      {REVIEWS.map((review) => (
        <article key={review.name} className="overflow-hidden rounded-[8px] border border-white/10 bg-cynos-card">
          <img src={review.img} alt={review.name} className="aspect-square w-full object-cover" loading="lazy" decoding="async" sizes="(max-width: 768px) 100vw, 33vw" />
          <div className="p-5">
            <div className="flex items-center justify-between">
              <span className="font-bold uppercase text-sm">{review.name}</span>
              <div className="flex gap-0.5">
                {[...Array(review.rating)].map((_, i) => <Star key={i} size={12} fill="#FF3333" stroke="#FF3333" />)}
              </div>
            </div>
            <p className="text-zinc-300 mt-2 text-sm">"{review.body}"</p>
          </div>
        </article>
      ))}
    </div>
  </section>
);

const TrustItem = ({ icon: Icon, label }) => (
  <div className="flex min-h-[54px] items-center gap-2 rounded-[8px] border border-white/10 bg-white/[0.03] px-3 py-3">
    <Icon className="text-cynos-red" size={16} />
    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-300">{label}</span>
  </div>
);

export default ProductDetailsPage;
